import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Grid, Row, Col, Panel, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import dateFormat from 'dateformat';
import { fetchApi } from '../../Network/NetworkService';
import SimpleChart from '../Ui/SimpleChart';

const todayYMD = () => new Date().toISOString().slice(0, 10);

const DoctorRefusalsDaily = () => {
    const [series, setSeries] = useState({ daily: [], topDoctorsDaily: [], totals: {} });
    const [feed, setFeed] = useState({ date: todayYMD(), events: [] });
    const [seriesLoading, setSeriesLoading] = useState(false);
    const [feedLoading, setFeedLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        end: todayYMD(),
        feedDate: todayYMD()
    });
    const latestSeriesFetchRef = useRef(0);
    const latestFeedFetchRef = useRef(0);

    const fetchSeries = useCallback(async () => {
        const fetchId = ++latestSeriesFetchRef.current;
        setSeriesLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ start: filters.start, end: filters.end });
            const res = await fetchApi(`/doctor-cancellations/refusals-timeseries?${params}`, 'GET');
            if (fetchId !== latestSeriesFetchRef.current) return;
            if (res && res.success) {
                setSeries(res.data);
            } else {
                setError((res && res.message) || 'Erreur chargement timeseries');
            }
        } catch (err) {
            if (fetchId === latestSeriesFetchRef.current) setError(`Erreur: ${err.message}`);
        } finally {
            if (fetchId === latestSeriesFetchRef.current) setSeriesLoading(false);
        }
    }, [filters.start, filters.end]);

    const fetchFeed = useCallback(async () => {
        const fetchId = ++latestFeedFetchRef.current;
        setFeedLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ date: filters.feedDate });
            const res = await fetchApi(`/doctor-cancellations/refusals-feed?${params}`, 'GET');
            if (fetchId !== latestFeedFetchRef.current) return;
            if (res && res.success) {
                setFeed(res.data);
            } else {
                setError((res && res.message) || 'Erreur chargement feed');
            }
        } catch (err) {
            if (fetchId === latestFeedFetchRef.current) setError(`Erreur: ${err.message}`);
        } finally {
            if (fetchId === latestFeedFetchRef.current) setFeedLoading(false);
        }
    }, [filters.feedDate]);

    useEffect(() => { fetchSeries(); }, [fetchSeries]);
    useEffect(() => { fetchFeed(); }, [fetchFeed]);

    const dailyChart = useMemo(() => {
        if (!series.daily.length) return null;
        // Split rawRefusals into "non-flip" + "flip" so the stack height equals
        // rawRefusals (a flip is itself a refusal — adding both series would
        // double-count).
        return {
            categories: series.daily.map(d => d.date.slice(5)), // MM-DD
            series: [
                {
                    name: 'Refus retenus par le seuil',
                    data: series.daily.map(d => Math.max(0, d.rawRefusals - d.thresholdFlips))
                },
                {
                    name: "Refus déclenchant la bascule",
                    data: series.daily.map(d => d.thresholdFlips)
                }
            ]
        };
    }, [series.daily]);

    const topDoctorsChart = useMemo(() => {
        if (!series.topDoctorsDaily || !series.topDoctorsDaily.length) return null;
        // Use series.daily as the canonical date axis — same eachDayUTC source
        // as the per-doctor arrays, so they're guaranteed aligned.
        const dates = series.daily.map(d => d.date.slice(5));
        return {
            categories: dates,
            series: series.topDoctorsDaily.map(d => ({
                name: d.doctorName,
                data: d.daily.map(x => x.count)
            }))
        };
    }, [series.topDoctorsDaily, series.daily]);

    const totals = series.totals || {};

    return (
        <Grid fluid>
            <Row>
                <Col xs={12}>
                    <h2>Refus quotidiens</h2>
                    <p className="text-muted">
                        Visualisation jour par jour des refus enregistrés via le seuil-gating
                        (<code>refusals[].refusedAt</code>). La barre rouge isole les bascules vers
                        <em> impossible</em> : un nombre faible vs. les refus bruts indique que
                        le seuil retient bien le manque à gagner. Vide tant que{' '}
                        <code>FEATURE_REFUSAL_THRESHOLD</code> n'est pas activé côté serveur.
                    </p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="alert alert-danger alert-dismissible">
                            <button type="button" className="close" onClick={() => setError(null)}>
                                <span>&times;</span>
                            </button>
                            {error}
                        </div>
                    </Col>
                </Row>
            )}

            <Row className="mb-4">
                <Col md={3}>
                    <div className="form-group">
                        <label>Période — début</label>
                        <input type="date" className="form-control"
                            value={filters.start}
                            onChange={(e) => setFilters(prev => ({ ...prev, start: e.target.value }))} />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Période — fin</label>
                        <input type="date" className="form-control"
                            value={filters.end}
                            onChange={(e) => setFilters(prev => ({ ...prev, end: e.target.value }))} />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group">
                        <label>Jour à explorer (feed)</label>
                        <input type="date" className="form-control"
                            value={filters.feedDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, feedDate: e.target.value }))} />
                    </div>
                </Col>
                <Col md={3}>
                    <div className="form-group" style={{ paddingTop: '25px' }}>
                        <Button
                            bsStyle="primary"
                            onClick={() => { fetchSeries(); fetchFeed(); }}
                            disabled={seriesLoading || feedLoading}
                        >
                            {(seriesLoading || feedLoading) ? 'Chargement...' : 'Actualiser'}
                        </Button>
                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Refus bruts (période)</h5>
                        <h2 style={{ color: '#f0ad4e', margin: 0 }}>{totals.rawRefusals || 0}</h2>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Bascules &laquo; impossible &raquo;</h5>
                        <h2 style={{ color: '#d9534f', margin: 0 }}>{totals.thresholdFlips || 0}</h2>
                        <small className="text-muted">
                            {totals.rawRefusals > 0
                                ? `${((totals.thresholdFlips / totals.rawRefusals) * 100).toFixed(1)}% des refus bruts`
                                : '—'}
                        </small>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Médecins refusants distincts</h5>
                        <h2 style={{ margin: 0 }}>{totals.distinctDoctors || 0}</h2>
                    </Panel.Body></Panel>
                </Col>
                <Col md={3}>
                    <Panel><Panel.Body>
                        <h5 className="text-muted" style={{ marginTop: 0 }}>Refus du {filters.feedDate}</h5>
                        <h2 style={{ margin: 0 }}>{feed.events.length}</h2>
                    </Panel.Body></Panel>
                </Col>
            </Row>

            {dailyChart && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading>
                                <h4>Refus quotidiens — retenus par le seuil vs. bascules</h4>
                                <small className="text-muted">
                                    La hauteur totale = refus bruts du jour. Le segment rouge
                                    représente les refus qui ont fait basculer une requête en
                                    "impossible" (un par requête flippée).
                                </small>
                            </Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="bar"
                                    height={320}
                                    categories={dailyChart.categories}
                                    series={dailyChart.series}
                                    options={{
                                        // Stack both series so the threshold-flip bar
                                        // sits inside the raw-refusals bar.
                                        series: dailyChart.series.map((s, i) => ({
                                            name: s.name,
                                            type: 'bar',
                                            stack: 'refusals',
                                            data: s.data,
                                            color: i === 0 ? '#f0ad4e' : '#d9534f'
                                        }))
                                    }}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            {topDoctorsChart && (
                <Row className="mb-4">
                    <Col xs={12}>
                        <Panel>
                            <Panel.Heading><h4>Top 10 médecins — refus quotidiens</h4></Panel.Heading>
                            <Panel.Body>
                                <SimpleChart
                                    type="line"
                                    height={360}
                                    categories={topDoctorsChart.categories}
                                    series={topDoctorsChart.series}
                                />
                            </Panel.Body>
                        </Panel>
                    </Col>
                </Row>
            )}

            <Row>
                <Col xs={12}>
                    <Panel>
                        <Panel.Heading>
                            <h4>Refus du {filters.feedDate} ({feed.events.length})</h4>
                            <small className="text-muted">
                                Liste des entrées <code>refusals[]</code> ajoutées ce jour-là.
                                Le badge <span className="label label-danger">bascule</span> marque
                                le refus qui a fait franchir le seuil et déclenché l'email patient.
                            </small>
                        </Panel.Heading>
                        <Panel.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {feedLoading && <p className="text-muted">Chargement...</p>}
                            {!feedLoading && feed.events.length === 0 && (
                                <p className="text-muted">Aucun refus enregistré ce jour-là.</p>
                            )}
                            {!feedLoading && feed.events.length > 0 && (
                                <table className="table table-bordered table-hover table-condensed">
                                    <thead>
                                        <tr>
                                            <th>Heure</th>
                                            <th>Médecin</th>
                                            <th>Patient</th>
                                            <th>Type</th>
                                            <th>Raison</th>
                                            <th style={{ textAlign: 'center' }}>État</th>
                                            <th style={{ textAlign: 'right' }}>#refus</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {feed.events.map((ev, i) => (
                                            <tr key={`${ev.requestId}-${ev.doctorId}-${i}`}>
                                                <td>{dateFormat(ev.refusedAt, 'HH:MM')}</td>
                                                <td>
                                                    <Link to={`/doctors/detail/${ev.doctorId}`}>
                                                        {ev.doctorName}
                                                    </Link>
                                                </td>
                                                <td>
                                                    {ev.userId ? (
                                                        <Link to={`/users/detail/${ev.userId}`}>
                                                            {ev.userName || ev.userEmail || 'Patient'}
                                                        </Link>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td>{ev.requestType}</td>
                                                <td style={{ maxWidth: '350px' }}>
                                                    {ev.reasonText
                                                        ? <small>{ev.reasonText}</small>
                                                        : <small className="text-muted">(aucune raison saisie)</small>}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {ev.isThresholdFlip
                                                        ? <span className="label label-danger" title="Refus qui a fait basculer la requête en impossible">bascule</span>
                                                        : <span className="label label-warning">refus</span>}
                                                </td>
                                                <td style={{ textAlign: 'right' }}>
                                                    {ev.totalRefusalsOnRequest}
                                                </td>
                                                <td>
                                                    <Link
                                                        to={`/requests/detail/${ev.requestId}`}
                                                        className="btn btn-info btn-xs"
                                                    >
                                                        Voir
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>
        </Grid>
    );
};

export default DoctorRefusalsDaily;
